/**
 * Redirect sanitization for authentication flows.
 *
 * The ?redirect= query parameter is attacker-controllable (anyone can craft a
 * sign-in link), so it must never be allowed to navigate off-origin after a
 * successful login — that is an open-redirect phishing vector.
 */

/**
 * Returns `value` only if it is a same-origin absolute path; otherwise the
 * fallback. Rejects absolute URLs (`https://…`), protocol-relative URLs
 * (`//evil.example`), and backslash variants some browsers normalize to `//`.
 */
export function safeRedirect(
  value: string | null | undefined,
  fallback = "/",
): string {
  if (!value) {
    return fallback;
  }

  if (!value.startsWith("/")) {
    return fallback;
  }

  // "//host" is protocol-relative and "/\host" gets normalized to it.
  if (value.startsWith("//") || value.includes("\\")) {
    return fallback;
  }

  return value;
}

const AUTH_ROUTE_PATHS = new Set(["/sign-in", "/sign-up"]);

/**
 * Normalizes an already-sanitized destination that will be visited with a
 * live session. The proxy redirects authenticated users away from the exact
 * auth pages, which would strip callback query markers before providers see
 * them. Similarly named application paths remain valid destinations.
 */
export function normalizePostAuthRedirect(
  safeDestination: string,
  fallback = "/",
): string {
  try {
    const destination = new URL(safeDestination, "https://same-origin.invalid");
    return AUTH_ROUTE_PATHS.has(destination.pathname)
      ? fallback
      : safeDestination;
  } catch {
    return fallback;
  }
}
